import Big from 'big.js';
import _ from 'lodash';
import { useCallback } from 'react';
import { datadogLogs } from '@datadog/browser-logs';

import { UseCartPricingReturns } from 'hooks/use-cart-pricing/use-cart-pricing.types';
import CartItem from 'state/cart-item';

export const useCartItemPricing = ({ priceCartPricing, isCheckoutPage }): UseCartPricingReturns => {
  const getItemPrice = useCallback(
    (product: CartItem) => {
      const receiptItem = product.getReceiptItem();

      if (!receiptItem && priceCartPricing) {
        datadogLogs.logger.error('No receipt item found for product', { productId: product.id });
      }

      const useReceiptItem = priceCartPricing && receiptItem;

      let { basePrice } = product;
      if (useReceiptItem) {
        const totalItemPrice = isCheckoutPage ? receiptItem.summaryPrice : receiptItem.menuPrice;
        const itemBasePrice = totalItemPrice / (receiptItem.quantity ?? 1);

        if (!totalItemPrice) {
          basePrice = receiptItem.basePrice / 100;
        } else {
          basePrice = itemBasePrice / 100;
        }
      }

      const originalPrice = Big(basePrice).times(product.quantity).toFixed(2);
      let adjustedPrice: string;
      let bogoDiscountedPrice: string | null = null;

      if (useReceiptItem) {
        const discountInDollars = (receiptItem?.discounts?.total ?? 0) / 100;

        adjustedPrice = Big(originalPrice).minus(discountInDollars).toFixed(2);
      } else {
        const { bogoDiscount } = product;
        const { price } = product;
        const itemSaleAdjustment = Big(product.saleAdjustment ?? 0).times(-1);

        const saleAdjustment = !_.isNil(itemSaleAdjustment) && !itemSaleAdjustment.eq(0) ? itemSaleAdjustment : null;

        adjustedPrice = Big(price)
          .minus(saleAdjustment ?? 0)
          .toFixed(2);
        bogoDiscountedPrice =
          !_.isNil(bogoDiscount) && bogoDiscount > 0 ? Big(adjustedPrice).minus(bogoDiscount).toFixed(2) : null;
      }

      // Final price logic: if bogoDiscountedPrice exists, use it as the final discounted price
      // This handles the case where BOGO discounts should override the adjustedPrice
      // bogoDiscountedPrice is only used in ecomm-only menus at the moment
      const finalDiscountedPrice = bogoDiscountedPrice ?? adjustedPrice;

      return {
        originalPrice,
        adjustedPrice: finalDiscountedPrice,
        bogoDiscountedPrice,
      };
    },
    [priceCartPricing, isCheckoutPage]
  );

  return {
    getItemPrice,
  };
};
